/*
 * Person groups (audit Phase 3.3). Replaces wwwroot/js/PersonGroup/List.js,
 * Detail.js and _namespace.js on the Phase 3.1 core. No jQuery.
 *
 * One screen: the groups of a Domain, and a detail dialog that is three
 * editors in one body -- the header (name + description, committed together by
 * Save), the members, and the Domain scope. The last two write per row: each
 * add or remove is atomic server-side and the cross-domain integrity
 * interceptor may refuse one without touching the others, so batching them
 * would report a single verdict for several independent outcomes.
 *
 * Membership and scope interact: a member has to be reachable from one of the
 * group's bound Domains, so removing the last Domain leaves a group nothing can
 * be added to. Both removals say what they cost before they happen.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm, hideModalThen } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'PersonGroupModal';
const CREATE_FORM = 'PersonGroupCreateForm';
const CREATE_ALERT = 'PersonGroupCreateMessageContainer';
const DETAIL_ALERT = 'PersonGroupDetailMessageContainer';

function currentDomainId(): string {
    return (one('#CurrentDomainId') as HTMLInputElement | null)?.value || '0';
}

function groupId(): string {
    return (one('#PersonGroupId') as HTMLInputElement | null)?.value ?? '';
}

function busy(working: boolean): void {
    one('#PersonGroupBusyIndicator')?.classList.toggle('d-none', !working);
}

/** The picker-backed hidden field beside a "find…" button, as a positive id or 0. */
function pickedId(selector: string): number {
    const parsed = Number.parseInt((one(`.persongroup-modal ${selector}`) as HTMLInputElement | null)?.value ?? '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export const List = {
    ShowDetail(id: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `PersonGroup/Detail/${id}`,
            title: null,
            errorTitle: `Group ${id}`,
            errorMessage: 'Group Detail failed to execute:',
        });
    },

    NewGroup(): void {
        void ajaxModal({
            modal: MODAL,
            url: `PersonGroup/Create/${currentDomainId()}`,
            formId: CREATE_FORM,
            focusId: 'Name',
            errorTitle: 'New Person Group',
            errorMessage: 'Failed to render new group form',
        });
    },

    NewGroupSave(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        saving(true);
        void ajaxJson({
            url: 'PersonGroup',
            method: 'POST',
            data: serialize(form),
            alertInto: CREATE_ALERT,
            errorTitle: 'Failed to create group',
            onSuccess: () => {
                toast('success', 'Group created');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                // The form, not the alert div beside it: only the form carries a
                // div.validation-summary for the model-level ('') entry.
                if (modelState) setValidationModelState(modelState, `#${CREATE_FORM}`);
                toast('danger', 'Failed to create group', summaryOf(data));
                return true;
            },
            onComplete: () => saving(false),
        });
    },

    Reload(): Promise<void> {
        return ajaxSwap({
            container: 'ViewContainer',
            url: `PersonGroup/Domain/${currentDomainId()}`,
            alertTitle: 'Failed to reload groups',
        });
    },
};

export const Detail = {
    EnterEditMode(): void {
        const dialog = one('.persongroup-modal');
        dialog?.classList.add('editing');
        const name = dialog?.querySelector<HTMLInputElement>('.pg-name');
        name?.focus();
        name?.select();
    },

    /** Discard unsaved header edits by re-fetching the dialog. */
    CancelEdit(): void {
        Detail.Reload(groupId());
    },

    SaveHeader(): void {
        const id = groupId();
        const name = (one('.persongroup-modal .pg-name') as HTMLInputElement | null)?.value ?? '';
        const description = (one('.persongroup-modal .pg-description') as HTMLTextAreaElement | null)?.value ?? '';
        if (!name.trim()) {
            toast('danger', 'Name is required');
            return;
        }
        busy(true);
        void ajaxJson({
            url: `PersonGroup/${id}`,
            method: 'PUT',
            data: { Id: id, Name: name, Description: description },
            alertInto: DETAIL_ALERT,
            errorTitle: 'Failed to save group',
            onSuccess: () => {
                toast('success', 'Group saved');
                Detail.Reload(id);
            },
            onFailure: failToast('Failed to save group'),
            onComplete: () => busy(false),
        });
    },

    Delete(): void {
        const id = groupId();
        void confirm({
            title: 'Delete Person Group',
            html: 'Delete this group?<br/><br/>'
                + 'Members keep their other groups, but <span class="text-danger">any licence assigned to '
                + 'this group is removed</span>, so anyone covered only through it loses that coverage.',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                busy(true);
                void ajaxJson({
                    url: `PersonGroup/${id}`,
                    method: 'DELETE',
                    alertInto: DETAIL_ALERT,
                    errorTitle: 'Failed to delete group',
                    onSuccess: () => {
                        toast('success', 'Group deleted');
                        hideModalThen(MODAL, () => void List.Reload());
                    },
                    onFailure: failToast('Failed to delete group'),
                    onComplete: () => busy(false),
                });
            },
        });
    },

    AddMember(): void {
        const id = groupId();
        const personId = pickedId('.pg-add-member-id');
        if (!personId) {
            toast('warning', 'Pick a person', 'Use the search button to pick one before pressing Add.');
            return;
        }
        void ajaxJson({
            url: `PersonGroup/${id}/Member/${personId}`,
            method: 'POST',
            errorTitle: 'Failed to add member',
            onSuccess: () => {
                toast('success', 'Member added');
                Detail.Reload(id);
            },
            onFailure: failToast('Failed to add member'),
        });
    },

    RemoveMember(personId: number | string): void {
        const id = groupId();
        void confirm({
            title: 'Remove member',
            message: 'Remove this member from the group? They keep their other groups, and lose any licence '
                + 'coverage that reaches them only through this one.',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `PersonGroup/${id}/Member/${personId}`,
                    method: 'DELETE',
                    errorTitle: 'Failed to remove member',
                    onSuccess: () => {
                        toast('success', 'Member removed');
                        Detail.Reload(id);
                    },
                    onFailure: failToast('Failed to remove member'),
                });
            },
        });
    },

    AddDomain(): void {
        const id = groupId();
        const domainId = pickedId('.pg-add-domain-id');
        if (!domainId) {
            toast('warning', 'Pick a domain', 'Use the search button to pick one before pressing Add.');
            return;
        }
        void ajaxJson({
            url: `PersonGroup/${id}/Domain/${domainId}`,
            method: 'POST',
            errorTitle: 'Failed to add domain scope',
            onSuccess: () => {
                toast('success', 'Domain added');
                Detail.Reload(id);
            },
            onFailure: failToast('Failed to add domain'),
        });
    },

    /**
     * Remove a Domain from the group's scope. The warning depends on whether
     * it is the last one: the member picker unions candidates across the bound
     * Domains, so a group with no Domain scope is one nobody can be added to.
     */
    RemoveDomain(domainId: number | string): void {
        const id = groupId();
        const isLast = (one('#PersonGroupDomainsTable')?.querySelectorAll('tbody tr[data-domain-id]').length ?? 0) === 1;
        void confirm({
            title: 'Remove domain scope',
            html: isLast
                ? 'Remove the last domain from this group\'s scope?<br/><br/>'
                    + '<span class="text-danger">Existing members stay, but no one can be added until a '
                    + 'domain is bound again — the member picker has nowhere to look.</span>'
                : 'Remove this domain from the group\'s scope? Members from that domain stay in the group.',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `PersonGroup/${id}/Domain/${domainId}`,
                    method: 'DELETE',
                    errorTitle: 'Failed to remove domain',
                    onSuccess: () => {
                        toast('success', 'Domain removed');
                        Detail.Reload(id);
                    },
                    onFailure: failToast('Failed to remove domain'),
                });
            },
        });
    },

    /**
     * Re-fetch the open dialog. Per-row writes change what the OTHER tab can
     * offer -- the member picker is scoped to the bound Domains -- and the
     * counts in the tab strip are rendered server-side, so appending a row
     * would leave the dialog describing a state that no longer holds.
     */
    Reload(id: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `PersonGroup/Detail/${id}`,
            title: null,
            errorTitle: 'Failed to reload group',
        });
    },
};

/** The create button's icon swap; the footer sits outside the form. */
function saving(working: boolean): void {
    const modal = one(`#${MODAL}`);
    if (!modal) return;
    for (const node of Array.from(modal.querySelectorAll('.fa-save'))) node.classList.toggle('d-none', working);
    for (const node of Array.from(modal.querySelectorAll('.fa-spinner:not(#PersonGroupBusyIndicator)'))) {
        node.classList.toggle('d-none', !working);
    }
}
